import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { Card, Table, Tag } from 'antd';

import {
  DashboardOrg,
  DashboardSummary,
  DashboardTicket,
} from '@totem/components/ticketing/ticketDashboard/types';

type Props = {
  tickets: DashboardTicket[];
  summary: DashboardSummary;
  orgs: Record<string, DashboardOrg>;
  isFiltered: boolean;
};

interface KpiCardProps {
  label: string;
  value: string | number;
  sub: string;
  valueColor?: string;
}

const KpiCard = ({ label, value, sub, valueColor }: KpiCardProps) => (
  <div
    style={{
      background: 'var(--ant-color-bg-container)',
      border: '1px solid var(--ant-color-border)',
      borderRadius: 10,
      padding: '12px 14px',
      boxShadow: '0 1px 2px rgba(0,0,0,.05), 0 4px 12px rgba(0,0,0,.04)',
      minWidth: 0,
    }}
  >
    <div
      style={{
        fontSize: 10.5,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'var(--ant-color-text-secondary)',
        fontWeight: 600,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: 26,
        fontWeight: 700,
        color: valueColor ?? 'var(--ant-color-text)',
        marginTop: 4,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </div>
    <div
      style={{
        fontSize: 11,
        color: 'var(--ant-color-text-secondary)',
        marginTop: 6,
      }}
    >
      {sub}
    </div>
  </div>
);

const SummaryTab = ({ tickets, summary, orgs, isFiltered }: Props) => {
  const kpis = summary.k;

  const displayKpis = !isFiltered
    ? kpis
    : {
        open: tickets.length,
        p1: tickets.filter((tkt) => tkt.p === 'P1').length,
        br: tickets.filter((tkt) => tkt.br === 1).length,
        r24: tickets.filter((tkt) => tkt.r24 === 1).length,
        un: tickets.filter((tkt) => tkt.ha === 0).length,
        stale14: tickets.filter((tkt) => tkt.stlD > 14 && tkt.stlD <= 30)
          .length,
        pr: tickets.filter((tkt) => tkt.pr === 1).length,
        orgs: 1,
        avgAge: tickets.length
          ? Math.round(
              (tickets.reduce((sum, tkt) => sum + tkt.ageD, 0) /
                tickets.length) *
                10,
            ) / 10
          : 0,
        oldest: tickets.reduce((maxAge, tkt) => Math.max(maxAge, tkt.ageD), 0),
        noCat: tickets.filter((tkt) => !tkt.cat).length,
        noBldg: tickets.filter((tkt) => !tkt.b).length,
        stale30: tickets.filter((tkt) => tkt.stlD > 30).length,
        respPct: kpis.respPct,
        created30: kpis.created30,
        closed30: kpis.closed30,
      };

  const typeData = Object.entries(
    tickets.reduce<Record<string, number>>((acc, tkt) => {
      acc[tkt.ty || '(none)'] = (acc[tkt.ty || '(none)'] || 0) + 1;
      return acc;
    }, {}),
  ).sort((lhs, rhs) => rhs[1] - lhs[1]);

  const catData = Object.entries(
    tickets.reduce<Record<string, number>>((acc, tkt) => {
      acc[tkt.cat || '(none)'] = (acc[tkt.cat || '(none)'] || 0) + 1;
      return acc;
    }, {}),
  ).sort((lhs, rhs) => rhs[1] - lhs[1]);

  const CHART_PALETTE = [
    '#122048',
    '#E8540A',
    '#3b82f6',
    '#22c55e',
    '#f59e0b',
    '#c4956a',
    '#6b7280',
    '#ef4444',
    '#1a2d5a',
    '#94a3b8',
  ];

  const TICKET_URL_BASE = 'https://dashboard.ib-services.net/dashboard/ticket/';

  const barChartOptions = (labels: string[]): ApexCharts.ApexOptions => ({
    chart: {
      type: 'bar',
      toolbar: { show: false },
      animations: { enabled: false },
    },
    plotOptions: {
      bar: { horizontal: true, borderRadius: 3, distributed: true },
    },
    xaxis: { categories: labels },
    dataLabels: { enabled: true },
    colors: CHART_PALETTE,
    legend: { show: false },
  });

  const donutChartOptions = (labels: string[]): ApexCharts.ApexOptions => ({
    chart: {
      type: 'donut',
      toolbar: { show: false },
      animations: { enabled: false },
    },
    labels,
    colors: CHART_PALETTE,
    dataLabels: { enabled: true },
    legend: { position: 'bottom', fontSize: '11px' },
    plotOptions: {
      pie: { donut: { size: '55%' } },
    },
  });

  const topFindings = [...tickets]
    .sort((lhs, rhs) => rhs.u - lhs.u)
    .slice(0, 5);

  const priorityColor: Record<string, string> = {
    P1: '#7f1d1d',
    P2: '#dc2626',
    P3: '#E8540A',
    P4: '#2563eb',
    P5: '#64748b',
  };

  const orgColumns = [
    {
      title: 'Customer',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <strong>{name}</strong>,
    },
    {
      title: 'Open',
      dataIndex: ['m', 'open'],
      key: 'open',
      align: 'right' as const,
    },
    {
      title: 'P1',
      dataIndex: ['m', 'p1'],
      key: 'p1',
      align: 'right' as const,
      render: (val: number) =>
        val > 0 ? (
          <span style={{ color: '#9C2A2A', fontWeight: 700 }}>{val}</span>
        ) : (
          0
        ),
    },
    {
      title: 'Past SLA',
      dataIndex: ['m', 'br'],
      key: 'br',
      align: 'right' as const,
      render: (val: number) => (val > 0 ? <Tag color="red">{val}</Tag> : 0),
    },
    {
      title: 'Unowned',
      dataIndex: ['m', 'un'],
      key: 'un',
      align: 'right' as const,
    },
    {
      title: 'Oldest (d)',
      dataIndex: ['m', 'oldest'],
      key: 'oldest',
      align: 'right' as const,
    },
  ];

  const orgRows = Object.values(orgs)
    .filter((org) => org.m.open > 0)
    .sort((lhs, rhs) => rhs.m.open - lhs.m.open);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
          gap: 10,
        }}
      >
        <KpiCard
          label="Open"
          value={displayKpis.open}
          sub="Total open tickets"
        />
        <KpiCard
          label="P1 Critical"
          value={displayKpis.p1}
          sub="Highest priority"
          valueColor={displayKpis.p1 > 0 ? '#9C2A2A' : undefined}
        />
        <KpiCard
          label="Past SLA"
          value={displayKpis.br}
          sub="SLA threshold exceeded"
          valueColor={displayKpis.br > 0 ? '#C94442' : undefined}
        />
        <KpiCard
          label="At Risk 24h"
          value={displayKpis.r24}
          sub="Breach within 24 hours"
          valueColor={displayKpis.r24 > 0 ? '#C88A2B' : undefined}
        />
        <KpiCard
          label="Unowned"
          value={displayKpis.un}
          sub="No assignee"
          valueColor={displayKpis.un > 0 ? '#C94442' : undefined}
        />
        <KpiCard
          label="Stale >14d"
          value={displayKpis.stale14}
          sub="No update in 14–30 days"
          valueColor={displayKpis.stale14 > 0 ? '#C88A2B' : undefined}
        />
        <KpiCard
          label="Proactive"
          value={displayKpis.pr}
          sub="System-generated"
          valueColor={displayKpis.pr > 0 ? '#2F9E6B' : undefined}
        />
        <KpiCard
          label="Customers"
          value={displayKpis.orgs}
          sub="With open tickets"
        />
      </div>

      {/* Row 2: Top Urgency Findings  |  Customers with Open Tickets */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.4fr 1fr',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <Card
          title={
            <span>
              Top Urgency Findings
              <Tag
                style={{ marginLeft: 8, fontVariantNumeric: 'tabular-nums' }}
              >
                {topFindings.length}
              </Tag>
            </span>
          }
          size="small"
          style={{ borderLeft: '3px solid #b91c1c' }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {topFindings.length === 0 && (
              <span style={{ color: 'var(--ant-color-text-secondary)' }}>
                No open tickets
              </span>
            )}
            {topFindings.map((tkt) => (
              <div
                key={tkt.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '80px 1fr auto',
                  gap: 10,
                  alignItems: 'start',
                  paddingBottom: 10,
                  borderBottom: '1px solid var(--ant-color-border)',
                }}
              >
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 3 }}
                >
                  <Tag
                    style={{
                      backgroundColor: priorityColor[tkt.p] || '#64748b',
                      color: '#fff',
                      fontWeight: 700,
                      border: 'none',
                      width: 'fit-content',
                    }}
                  >
                    {tkt.p}
                  </Tag>
                  {tkt.br === 1 && (
                    <Tag
                      color="error"
                      style={{ width: 'fit-content', fontSize: 10 }}
                    >
                      PAST SLA
                    </Tag>
                  )}
                  {tkt.r24 === 1 && tkt.br === 0 && (
                    <Tag
                      color="warning"
                      style={{ width: 'fit-content', fontSize: 10 }}
                    >
                      @ RISK
                    </Tag>
                  )}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--ant-color-text-secondary)',
                    }}
                  >
                    {orgs[tkt.o]?.name ?? tkt.o}
                    {' · '}
                    <a
                      href={`${TICKET_URL_BASE}${tkt.id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {tkt.num}
                    </a>
                  </div>
                  <div style={{ fontSize: 12, marginTop: 2 }}>{tkt.sum}</div>
                </div>
                <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color:
                        tkt.ageD > 60
                          ? '#b91c1c'
                          : tkt.ageD > 14
                            ? '#E8540A'
                            : 'var(--ant-color-text-secondary)',
                    }}
                  >
                    {tkt.ageD.toFixed(1)}d
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: 'var(--ant-color-text-secondary)',
                      marginTop: 2,
                    }}
                  >
                    u {tkt.u.toFixed(0)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Customers with Open Tickets" size="small">
          <Table
            dataSource={orgRows}
            columns={orgColumns}
            rowKey="id"
            size="small"
            pagination={false}
          />
        </Card>
      </div>

      {/* Row 3: Open by Category  |  Open by Type */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <Card title="Open by Category" size="small">
          {catData.length > 0 ? (
            <ReactApexChart
              type="donut"
              height={260}
              series={catData.map((entry) => entry[1])}
              options={donutChartOptions(catData.map((entry) => entry[0]))}
            />
          ) : (
            <div style={{ color: '#94A3B8', padding: 16 }}>No data</div>
          )}
        </Card>
        <Card title="Open by Type" size="small">
          {typeData.length > 0 ? (
            <ReactApexChart
              type="bar"
              height={260}
              series={[
                { name: 'Tickets', data: typeData.map((entry) => entry[1]) },
              ]}
              options={barChartOptions(typeData.map((entry) => entry[0]))}
            />
          ) : (
            <div style={{ color: '#94A3B8', padding: 16 }}>No data</div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default SummaryTab;
